import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { hoechstmengenAlsPromptZeile } from "../data/hoechstmengen";
import {
  AGENT_PERSONALITIES,
  buildUserContext,
  buildCurrentSituation,
} from "../agentPersonalities";

const MAX_CONTEXT_MESSAGES = 30;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// ─────────────────────────────────────────────
// AGENT MEMORY BUILDER
// Baut aus den letzten Gesprächen ein
// strukturiertes Gedächtnis für den System-Prompt
// ─────────────────────────────────────────────
function buildMemoryContext(messages) {
  if (!messages || messages.length === 0) return "";

  // Letzte 7 Tage Gesprächs-Highlights extrahieren
  const userMessages = messages
    .filter(m => m.role === "user" && !m.content.startsWith("[PROACTIVE]"))
    .slice(-10)
    .map(m => `- "${m.content.slice(0, 100)}"`)
    .join("\n");

  if (!userMessages) return "";

  return `
GESPRÄCHS-GEDÄCHTNIS (letzte Nachrichten des Users):
${userMessages}
  `.trim();
}

// ─────────────────────────────────────────────
// HABIT RECOMMENDATION ENGINE
// ─────────────────────────────────────────────
function buildHabitRecommendations(habitData) {
  if (!habitData?.insights) return "";

  const { insights, allHabits } = habitData;
  const weakHabits = insights?.habitStats
    ?.filter(h => h.rate < 40)
    ?.slice(0, 3)
    ?.map(h => h.label) || [];

  const strongHabits = insights?.habitStats
    ?.filter(h => h.rate > 80)
    ?.slice(0, 3)
    ?.map(h => h.label) || [];

  if (!weakHabits.length && !strongHabits.length) return "";

  return `
HABIT-ANALYSE:
Stark (>80% Konsistenz): ${strongHabits.join(", ") || "noch keine Daten"}
Schwach (<40% Konsistenz): ${weakHabits.join(", ") || "keine"}
  `.trim();
}

// ─────────────────────────────────────────────
// CLAUDE API CALL — via Supabase Proxy
// Mit vollem Multi-Turn Kontext
// ─────────────────────────────────────────────
async function callClaudeProxy({
  system,
  messages,
  maxTokens = 400,
}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Nicht eingeloggt");

  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/claude-proxy`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
        "apikey": SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ system, messages, max_tokens: maxTokens }),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Fehler ${response.status}`);
  }

  const data = await response.json();
  return data.content?.[0]?.text || "...";
}

// ─────────────────────────────────────────────
// useAgent HOOK
// ─────────────────────────────────────────────
export function useAgent(userId, habitData) {
  const [personality, setPersonality] = useState("friend");
  const [messages, setMessages]       = useState([]);
  const [isTyping, setIsTyping]       = useState(false);
  const [loading, setLoading]         = useState(true);
  const [weeklyReview, setWeeklyReview] = useState(null);
  const [habitSuggestion, setHabitSuggestion] = useState(null);

  // ── Init ──────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    Promise.all([loadSettings(), loadRecentMessages()]);
  }, [userId]);

  async function loadSettings() {
    const { data } = await supabase
      .from("agent_settings")
      .select("personality")
      .eq("user_id", userId)
      .single();
    if (data?.personality) setPersonality(data.personality);
    setLoading(false);
  }

  async function loadRecentMessages() {
    const { data } = await supabase
      .from("agent_messages")
      .select("id, role, content, personality, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(MAX_CONTEXT_MESSAGES);
    if (data) setMessages(data.reverse().map(formatMessage));
  }

  // ── System-Prompt bauen ───────────────────
  function buildSystemPrompt(p, extraContext = "") {
    const userCtx    = buildUserContext(habitData?.user, habitData);
    const situation  = buildCurrentSituation(messages);
    const memory     = buildMemoryContext(messages);
    const habitRecs  = buildHabitRecommendations(habitData);

    return p.systemPrompt
      .replace("{user_context}", `${userCtx}\n\n${habitRecs}\n\n${memory}`)
      .replace("{current_situation}", situation)
      + (extraContext ? `\n\n${extraContext}` : "");
  }

  // ── Persönlichkeit wechseln ───────────────
  const changePersonality = useCallback(async (newP) => {
    setPersonality(newP);
    await supabase
      .from("agent_settings")
      .upsert({ user_id: userId, personality: newP }, { onConflict: "user_id" });

    const intros = {
      coach:     "Neuer Style. Ich bin dein Coach ab jetzt. Wo stehst du gerade wirklich?",
      friend:    "Hey! Freut mich dass wir jetzt so reden. Wie läuft es bei dir?",
      scientist: "Interessanter Wechsel. Ich habe deine Daten bereits analysiert. Bereit für Einblicke?",
      monk:      "Eine neue Stimme. Aber derselbe Weg. Was beschäftigt dich gerade wirklich?",
    };

    const introMsg = {
      id: `intro_${Date.now()}`,
      role: "assistant",
      content: intros[newP] || "Hallo. Wie kann ich dir helfen?",
      timestamp: new Date(),
      personality: newP,
    };
    setMessages(prev => [...prev, introMsg]);
  }, [userId]);

  // ── Nachricht senden ──────────────────────
  const sendMessage = useCallback(async (content, image = null) => {
    if (!content.trim() || isTyping) return;

    const userMsg = {
      id:           `user_${Date.now()}`,
      role:         "user",
      content:      content.trim(),
      timestamp:    new Date(),
      imagePreview: image?.preview || null,
    };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    await supabase.from("agent_messages").insert({
      user_id: userId, role: "user",
      content: content.trim(), personality,
    });

    try {
      const p = AGENT_PERSONALITIES[personality];

      const contextMessages = [...messages.slice(-14), userMsg]
        .filter(m => !m.content?.startsWith("[PROACTIVE]"))
        .map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }));

      // Mit Bild: Vision-Request
      const extraInstruction = image ? SUPPLEMENT_ANALYSIS_INSTRUCTION : "";
      const system = buildSystemPrompt(p, extraInstruction);

      const apiMessages = image
        ? buildVisionMessages(contextMessages.slice(0, -1), content, image)
        : contextMessages;

      const response = await callClaudeProxy({
        system,
        messages: apiMessages,
        maxTokens: image ? 600 : 400,
      });

      // Bei Supplement-Analyse: JSON parsen
      let finalContent = response;
      if (image) {
        try {
          const clean = response.replace(/```json|```/g, "").trim();
          JSON.parse(clean); // Validierung
          finalContent = clean;
        } catch {
          finalContent = response;
        }
      }

      const agentMsg = {
        id:                  `agent_${Date.now()}`,
        role:                "assistant",
        content:             finalContent,
        timestamp:           new Date(),
        personality,
        isSupplementAnalysis: !!image,
      };
      setMessages(prev => [...prev, agentMsg]);

      await supabase.from("agent_messages").insert({
        user_id: userId, role: "assistant",
        content: finalContent, personality,
      });

      await maybeSuggestHabit(content, response);

    } catch (err) {
      if (err.name === "AbortError") return;
      setMessages(prev => [...prev, {
        id: `err_${Date.now()}`,
        role: "assistant",
        content: "Ich konnte dich gerade nicht erreichen. Versuche es nochmal.",
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [userId, personality, habitData, messages, isTyping]);

  // ── Proaktive Begrüßung ───────────────────
  const loadProactiveGreeting = useCallback(async () => {
    if (!habitData || messages.length > 0) return;
    setIsTyping(true);
    try {
      const p = AGENT_PERSONALITIES[personality];
      const system = buildSystemPrompt(p);
      const greetingPrompt = `Begrüße den User proaktiv und natürlich. Schau dir seinen Fortschritt von heute und seine Habit-Muster an. Starte ein echtes Gespräch — max. 2–3 Sätze. Keine generische Begrüßung.`;

      const response = await callClaudeProxy({
        system,
        messages: [{ role: "user", content: greetingPrompt }],
        maxTokens: 150,
      });

      setMessages([{
        id: `greet_${Date.now()}`,
        role: "assistant",
        content: response,
        timestamp: new Date(),
        personality,
        isGreeting: true,
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [personality, habitData, messages.length]);

  // ── Weekly Review generieren ──────────────
  const generateWeeklyReview = useCallback(async () => {
    if (!habitData?.insights) return;
    setIsTyping(true);
    try {
      const p = AGENT_PERSONALITIES[personality];
      const { insights } = habitData;

      const reviewPrompt = `
Erstelle ein detailliertes wöchentliches Review für den User. Nutze alle verfügbaren Daten.

Struktur (kompakt, max 250 Wörter):
1. Was diese Woche gut lief (konkret)
2. Was noch nicht rund läuft (ehrlich, nicht destruktiv)
3. Eine konkrete Empfehlung für die nächste Woche
4. Ein motivierender Abschluss der zum Weitermachen animiert

Wochendaten:
- Completion Rate: ${insights.completionRate}%
- Beste Streak: ${insights.bestStreak} Tage
- Stärkster Habit: ${insights.topHabit?.label || 'unbekannt'}
- Schwächster Habit: ${insights.missedHabit?.label || 'unbekannt'}
- Aktivste Tage: ${insights.bestDay}
- Gesamt erledigte Habits: ${insights.totalChecks}

Antworte im Stil deiner Persönlichkeit. Deutsch.
      `.trim();

      const system = buildSystemPrompt(p, "DU ERSTELLST GERADE DAS WÖCHENTLICHE REVIEW. Sei strukturiert aber in deinem Persönlichkeits-Stil.");
      const response = await callClaudeProxy({
        system,
        messages: [{ role: "user", content: reviewPrompt }],
        maxTokens: 500,
      });

      const reviewMsg = {
        id: `review_${Date.now()}`,
        role: "assistant",
        content: `[WEEKLY_REVIEW] ${response}`,
        timestamp: new Date(),
        personality,
        isWeeklyReview: true,
      };

      setMessages(prev => [...prev, reviewMsg]);
      setWeeklyReview(response);

      await supabase.from("agent_messages").insert({
        user_id: userId, role: "assistant",
        content: `[WEEKLY_REVIEW] ${response}`, personality,
      });

    } finally {
      setIsTyping(false);
    }
  }, [personality, habitData, userId, messages]);

  // ── Habit-Empfehlung ──────────────────────
  async function maybeSuggestHabit(userMessage, agentResponse) {
    const triggers = ["mehr tun", "was empfiehlst", "neuer habit", "was soll ich", "hilf mir", "vorschlag"];
    const shouldSuggest = triggers.some(t => userMessage.toLowerCase().includes(t));
    if (!shouldSuggest || !habitData?.insights) return;

    const weakHabits = habitData.insights?.habitStats
      ?.filter(h => h.rate < 40)?.slice(0, 2) || [];

    if (weakHabits.length === 0) return;

    const suggestion = {
      id: `suggest_${Date.now()}`,
      habits: weakHabits,
      message: `Basierend auf deinen Daten: Diese Habits haben Potenzial — ${weakHabits.map(h => h.label).join(" und ")}.`,
    };
    setHabitSuggestion(suggestion);
    setTimeout(() => setHabitSuggestion(null), 10000);
  }

  // ── Chat leeren ───────────────────────────
  const clearChat = useCallback(async () => {
    await supabase.from("agent_messages").delete().eq("user_id", userId);
    setMessages([]);
    setWeeklyReview(null);
  }, [userId]);

  return {
    personality,
    messages,
    isTyping,
    loading,
    weeklyReview,
    habitSuggestion,
    changePersonality,
    sendMessage,
    loadProactiveGreeting,
    generateWeeklyReview,
    clearChat,
  };
}

function formatMessage(row) {
  return {
    id: row.id,
    role: row.role,
    content: row.content,
    timestamp: new Date(row.created_at),
    personality: row.personality,
    isWeeklyReview: row.content?.startsWith("[WEEKLY_REVIEW]"),
  };
}

// ─────────────────────────────────────────────
// SUPPLEMENT ANALYSE INSTRUCTION
// ─────────────────────────────────────────────
const SUPPLEMENT_ANALYSIS_INSTRUCTION = `
Du liest ein Supplement-Etikett ab. Antworte NUR mit einem JSON-Objekt — kein Text davor oder danach, keine Markdown-Backticks.

JSON-Format (exakt so):
{
  "name": "Produktname kurz",
  "category": "magnesium",
  "form": "Magnesiumcitrat",
  "naehrstoffe": [
    { "stoff": "Magnesium", "menge": "300 mg pro Tagesportion", "status": "ueber" }
  ],
  "hinweise": ["Magnesium liegt über der BfR-Empfehlung für Nahrungsergänzungsmittel – Einnahme ärztlich abklären."]
}

Regeln:
- Nur ablesen, was auf dem Etikett steht. Nichts ergänzen oder schätzen. Was nicht lesbar ist: "unklar".
- category: einer von: "magnesium", "vitamin_d", "omega3", "zinc", "vitamin_c", "creatine", "ashwagandha", "protein", "vitamin_b", "iron", "calcium", "other"
- status pro Nährstoff, Menge pro empfohlener Tagesportion verglichen mit den BfR-Höchstmengen-Empfehlungen für Nahrungsergänzungsmittel pro Tag:
  ${hoechstmengenAlsPromptZeile()}
  "ok" = höchstens so viel, "ueber" = mehr, "unklar" = Menge unbekannt oder kein Wert in dieser Liste.
- hinweise: höchstens 3, je höchstens 20 Wörter, sachlich. Bei "ueber": darauf hinweisen und ärztliche Abklärung empfehlen. Bei Eisen, Ashwagandha, Johanniskraut, Melatonin, Hormonen oder Pflanzenextrakten mit Arzneibezug immer: "Vor der Einnahme ärztlich abklären."
- Keine Qualitätsbewertung, keine Punkte, keine Wirkversprechen, keine Einnahme-Zeitpunkte, keine Dosierungsempfehlung, keine Produkt- oder Kaufempfehlung.
- Alles auf Deutsch. NUR JSON, absolut nichts anderes.
`;


// ─────────────────────────────────────────────
// VISION MESSAGE BUILDER
// Baut die Claude API Message mit Bild als Base64
// ─────────────────────────────────────────────
function buildVisionMessages(contextMessages, userText, image) {
  // Kontext ohne das letzte User-Message (wird durch Vision-Message ersetzt)
  const history = contextMessages.slice(0, -1);

  const visionMessage = {
    role: "user",
    content: [
      {
        type: "image",
        source: {
          type:       "base64",
          media_type: image.mediaType || "image/jpeg",
          data:       image.base64,
        },
      },
      {
        type: "text",
        text: userText || "Analysiere dieses Supplement-Etikett bitte.",
      },
    ],
  };

  return [...history, visionMessage];
}